import { useCallback, useRef, useState } from "react"

import { BasicRichText, type BasicRichTextProps } from "./BasicRichText"
import { getMentions } from "./getMentions"
import { presets } from "./presets/presets"
import type { RemirrorMention } from "./ui/Mentions"

export type RichTextProps = {
  forType?: string
  forId?: string
  preset?: keyof typeof presets
  content?: string
  extraData?: any
  fullMentionLinks?: boolean
  onChange?: (html: string, textValue: string, extraData: any) => void
} & BasicRichTextProps

// forType and forId are required for uploads, however in cases such as the "simple" preset no upload is possible therefore they don't need to be added
// This is currently not enforced by the type system but can be gathered by the various examples of us using this component across the app
export const RichText = ({
  value,
  content,
  forType,
  forId,
  preset = "full",
  onChange,
  extraData,
  fullMentionLinks,
  ...rest
}: RichTextProps) => {
  const [mentionState, setMentionState] = useState<{
    mentions: Record<string, Array<RemirrorMention>>
    lastKind: string
    lastQuery: string
  }>(null)
  const timeout = useRef(null)

  const getThrottledMentions = useCallback((kind, query) => {
    if (timeout.current) {
      clearTimeout(timeout.current)
      timeout.current = null
    }

    timeout.current = setTimeout(() => {
      getMentions(kind, query)
        .then((result) => {
          setMentionState({
            lastKind: kind,
            lastQuery: query,
            mentions: result,
          })
          timeout.current = null
        })
        .catch(() => {})
    }, 300)
  }, [])

  const [chooser, setChooser] = useState({})

  const setChooserType = useCallback(
    (kind, open, cmd, text) => setChooser((prev) => ({ ...prev, [kind]: { open, cmd, text } })),
    [],
  )

  const handleChange = useCallback(
    (html: string, textValue: string) => {
      if (onChange) {
        return onChange(html, textValue, extraData)
      }
    },
    [onChange, extraData],
  )

  return (
    <BasicRichText
      {...rest}
      value={value || content}
      options={presets[preset].options}
      mentions={mentionState?.mentions}
      onSuggest={getThrottledMentions}
      extraMenuItems={presets[preset].getMenu(chooser, setChooserType, forType, forId)}
      onChange={handleChange}
      settings={{
        uploadOptions: { forType, forId },
        fullMentionLinks,
      }}
    />
  )
}

export const RichTextViewer = (
  props: Omit<BasicRichTextProps, "readOnly" | "value"> & {
    value?: string
    content?: string
  },
) => {
  return (
    <BasicRichText options={presets.full.options} value={props.value || props.content} {...props} readOnly={true} />
  )
}
